import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, QrCode } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { api, errMsg, img, inr, load } from "@/lib/api";
import { Cta } from "@/components/Cta";
import { Totals } from "@/components/BagDrawer";

const STATES = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"];

const RULES = {
  name: (v) => v.trim().length >= 2 || "Please enter your full name",
  mobile: (v) => /^[6-9]\d{9}$/.test(v.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "")) || "Enter a valid 10-digit mobile number",
  email: (v) => /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(v.trim()) || "Enter a valid email address",
  address: (v) => v.trim().length >= 5 || "Please enter your full address",
  city: (v) => v.trim().length >= 2 || "Please enter your city",
  state: (v) => !!v || "Please choose your state",
  pincode: (v) => /^[1-9]\d{5}$/.test(v.trim()) || "Enter a valid 6-digit PIN code",
};

const Field = ({ id, label, error, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-[14px] font-bold mb-2">{label}</span>
    {children}
    {error && <span className="block text-[13px] font-semibold text-ruby mt-1.5" data-testid={`checkout-error-${id}`}>{error}</span>}
  </label>
);

export default function Checkout() {
  const { cart, quote, quoteError, products, clearCart } = useStore();
  const nav = useNavigate();
  const [f, setF] = useState(() => load("rh_customer", { name: "", mobile: "", email: "", address: "", city: "", state: "", pincode: "" }));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setErrors({ ...errors, [k]: undefined }); };

  if (!cart.length)
    return (
      <main className="container-rh py-28 text-center" data-testid="checkout-empty">
        <p className="font-display text-[44px]">Your bag is empty</p>
        <p className="text-plum/70 mt-3 mb-10">Choose a piece from the Selected Three to begin.</p>
        <Cta to="/shop" label="Explore the edit" />
      </main>
    );

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    Object.entries(RULES).forEach(([k, r]) => { const ok = r(f[k]); if (ok !== true) errs[k] = ok; });
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error("Please check the highlighted details");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { items: cart, customer: f });
      localStorage.setItem("rh_customer", JSON.stringify(f));
      localStorage.setItem("rh_orders", JSON.stringify({ ...load("rh_orders", {}), [data.order_number]: data.token }));
      clearCart();
      nav(`/order/${data.order_number}?t=${data.token}`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  const input = (k, props = {}) => <input className="rh-input" value={f[k]} onChange={set(k)} aria-invalid={!!errors[k]} data-testid={`checkout-input-${k}`} {...props} />;

  return (
    <main className="container-rh py-10 lg:py-16" data-testid="checkout-page">
      <p className="eyebrow text-berry">Secure checkout</p>
      <h1 className="font-display font-medium text-[44px] sm:text-[56px] leading-none mt-3 mb-10">Almost yours.</h1>
      <form onSubmit={submit} noValidate className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <div className="lg:col-span-7 space-y-6">
          <section className="rounded-[22px] bg-ivory border border-plum/10 p-6 sm:p-8">
            <h2 className="font-display text-[28px] font-semibold mb-6">Your details</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field id="name" label="Full name" error={errors.name} className="sm:col-span-2">{input("name", { autoComplete: "name" })}</Field>
              <Field id="mobile" label="Mobile number" error={errors.mobile}>{input("mobile", { inputMode: "tel", autoComplete: "tel", placeholder: "10-digit mobile" })}</Field>
              <Field id="email" label="Email" error={errors.email}>{input("email", { type: "email", autoComplete: "email" })}</Field>
            </div>
          </section>
          <section className="rounded-[22px] bg-ivory border border-plum/10 p-6 sm:p-8">
            <h2 className="font-display text-[28px] font-semibold mb-6">Delivery address</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field id="address" label="House, street, area" error={errors.address} className="sm:col-span-2">
                <textarea rows={3} className="rh-input" value={f.address} onChange={set("address")} aria-invalid={!!errors.address} data-testid="checkout-input-address" autoComplete="street-address" />
              </Field>
              <Field id="city" label="City" error={errors.city}>{input("city", { autoComplete: "address-level2" })}</Field>
              <Field id="state" label="State" error={errors.state}>
                <select className="rh-input" value={f.state} onChange={set("state")} aria-invalid={!!errors.state} data-testid="checkout-input-state">
                  <option value="">Choose state</option>
                  {STATES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field id="pincode" label="PIN code" error={errors.pincode}>{input("pincode", { inputMode: "numeric", maxLength: 6, autoComplete: "postal-code" })}</Field>
            </div>
          </section>
          <section className="rounded-[22px] bg-ivory border border-plum/10 p-6 sm:p-8">
            <h2 className="font-display text-[28px] font-semibold mb-5">Payment</h2>
            <div className="flex items-start gap-4 rounded-[16px] border-2 border-plum p-4" data-testid="checkout-payment-upi">
              <span className="w-11 h-11 rounded-[12px] bg-plum text-champagne grid place-items-center flex-none"><QrCode className="w-5 h-5" /></span>
              <div>
                <p className="font-bold text-[16px]">UPI — scan & pay</p>
                <p className="text-[14px] text-plum/70 mt-1">After placing your order you'll get a QR code with the exact amount for this order. Pay with Google Pay, PhonePe, Paytm or any UPI app.</p>
              </div>
            </div>
          </section>
        </div>

        <aside className="lg:col-span-5 lg:sticky lg:top-[132px] rounded-[22px] bg-white border border-plum/10 p-6 sm:p-8" data-testid="checkout-summary">
          <h2 className="font-display text-[28px] font-semibold mb-4">Order summary</h2>
          <ul className="divide-y divide-plum/10 mb-5">
            {cart.map((c) => {
              const p = products.find((x) => x.id === c.product_id);
              if (!p) return null;
              return (
                <li key={c.product_id + c.variant} className="flex gap-4 py-4">
                  <img src={img(p.images[0])} alt="" className="w-16 h-20 rounded-[12px] object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-[20px] font-semibold leading-tight">{p.name}</p>
                    <p className="text-[14px] text-plum/70">{c.variant ? `${c.variant} · ` : ""}Qty {c.quantity}</p>
                  </div>
                  <p className="font-bold">{inr(p.effective_price * c.quantity)}</p>
                </li>
              );
            })}
          </ul>
          {quoteError && <p className="text-ruby font-semibold text-[14px] mb-4" data-testid="checkout-quote-error">{quoteError}</p>}
          {quote && <Totals quote={quote} />}
          <Cta type="submit" full size="lg" variant="berry" className="mt-6" testId="checkout-place-order" disabled={!quote} state={busy ? "loading" : "idle"} label={quote ? `Place order · ${inr(quote.total)}` : "Place order"} />
          <p className="mt-4 flex items-center justify-center gap-2 text-[13px] text-plum/60"><Lock className="w-3.5 h-3.5" /> Prices and stock are confirmed on our server</p>
          <Link to="/shop" className="block text-center mt-3 text-[14px] font-semibold underline underline-offset-4">Continue shopping</Link>
        </aside>
      </form>
    </main>
  );
}
